/**
 * StatsCard Component
 * Dashboard metric display card
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label: string;
  };
  loading?: boolean;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  loading = false,
  color = 'primary',
}) => {
  const getTrendColor = (value: number) => {
    return value >= 0 ? 'success.main' : 'error.main';
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={48} sx={{ mt: 1 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color === 'primary' ? '#667eea20' : '#764ba220'} 0%, transparent 100%)`,
        borderTop: 3,
        borderTopColor: `${color}.main`,
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontWeight: 500 }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ fontWeight: 700, my: 1 }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend.value >= 0 ? (
                  <TrendingUp sx={{ fontSize: 16, color: getTrendColor(trend.value), mr: 0.5 }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16, color: getTrendColor(trend.value), mr: 0.5 }} />
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: getTrendColor(trend.value),
                    fontWeight: 600,
                  }}
                >
                  {trend.value > 0 ? '+' : ''}{trend.value}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                  {trend.label}
                </Typography>
              </Box>
            )}
          </Box>
          {icon && (
            <Box 
              sx={{ 
                color: `${color}.main`,
                opacity: 0.8,
                ml: 2,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatsCard;